import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Upload, Trash2, Save, RefreshCw, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/settings/Card";
import { BrandLogo } from "@/components/BrandLogo";

export const BrandingSettings = () => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setName(data.company_name); setAbout(data.company_about); setEmail(data.company_email || ""); setAddress(data.company_address || ""); } }, [data]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["branding"] }); qc.invalidateQueries({ queryKey: ["settings"] }); };
  const save = async () => {
    try { await api.put("/settings", { company_name: name, company_about: about, company_email: email, company_address: address }); refresh(); toast.success("Dati aziendali salvati"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/settings/logo", fd); refresh(); toast.success("Logo caricato"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const removeLogo = async () => { await api.delete("/settings/logo"); refresh(); toast.success("Logo rimosso"); };
  const sync = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/settings/whatsapp/sync-profile"); r.ok ? toast.success(r.detail) : toast.error(r.detail); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-6 items-start">
      <Card testid="branding-card" title="Identità aziendale" desc="Nome, descrizione e logo mostrati nell'app e sul profilo WhatsApp" icon={Building2}>
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <BrandLogo size={72} className="border border-slate-200" />
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="branding-logo-file-input" />
              <div className="flex gap-2">
                <Button data-testid="branding-upload-logo-button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}><Upload size={14} className="mr-1.5" />Carica logo</Button>
                {data?.has_logo && <Button data-testid="branding-remove-logo-button" variant="ghost" size="sm" onClick={removeLogo}><Trash2 size={14} className="mr-1.5 text-red-500" />Rimuovi</Button>}
              </div>
              <p className="text-[11px] text-slate-400">PNG/JPG quadrato, min 192×192px, max 5MB</p>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Nome azienda</Label><Input data-testid="branding-company-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Info profilo WhatsApp ("About", max 139 caratteri)</Label><Input data-testid="branding-company-about-input" maxLength={139} value={about} onChange={(e) => setAbout(e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email privacy / contatto</Label><Input data-testid="branding-company-email-input" type="email" placeholder="privacy@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Sede legale (per la privacy)</Label><Input data-testid="branding-company-address-input" placeholder="Via…, Città" value={address} onChange={(e) => setAddress(e.target.value)} /></div>
          </div>
          <div className="flex justify-between gap-2 pt-1">
            <Button data-testid="branding-sync-whatsapp-button" variant="outline" disabled={busy} onClick={sync}><RefreshCw size={14} className="mr-1.5" />Sincronizza con profilo WhatsApp</Button>
            <Button data-testid="branding-save-button" onClick={save} className="bg-[#0F172A] hover:bg-[#1E293B]"><Save size={15} className="mr-1.5" />Salva</Button>
          </div>
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">La sincronizzazione aggiorna foto profilo e descrizione tramite Meta API (richiede credenziali e App ID). Il <b>nome visualizzato</b> su WhatsApp va approvato da Meta in WhatsApp Manager.</p>
        </div>
      </Card>
      <div className="bg-[#0B141A] rounded-2xl p-5 text-white" data-testid="branding-preview">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-4">Anteprima cliente</p>
        <div className="flex flex-col items-center text-center">
          <BrandLogo size={84} className="rounded-full" />
          <div className="mt-3 font-semibold flex items-center gap-1.5">{name || "—"}<span className="w-4 h-4 rounded-full bg-[#25D366] grid place-items-center text-[9px] text-slate-900">✓</span></div>
          <div className="text-xs text-slate-400">Account aziendale</div>
          <div className="mt-4 w-full bg-[#1F2C34] rounded-lg p-3 text-left text-sm">
            <div className="text-[10px] text-slate-400 mb-1">Info</div>{about || "—"}
          </div>
          <div className="mt-3 w-full flex items-center gap-2 bg-[#005C4B] rounded-lg rounded-tr-sm p-2.5 text-xs text-left"><MessageCircle size={13} />Ciao! Come possiamo aiutarti?</div>
          <div className="mt-5 w-full text-left">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">Icona app installata</p>
            <div className="flex items-end gap-3">
              <img data-testid="branding-app-icon-preview" src={`/api/branding/icon/192.png?v=${data?.has_logo ? data?.logo_version : "default"}`} alt="Icona app" className="w-14 h-14 rounded-2xl" />
              <img src={`/api/branding/icon/maskable-512.png?v=${data?.has_logo ? data?.logo_version : "default"}`} alt="Icona Android" className="w-10 h-10 rounded-full" />
            </div>
            <p className="text-[10px] text-slate-500 mt-2">Si aggiorna in automatico su Chrome/Edge. Su iPhone e Safari reinstalla l'app per vedere la nuova icona.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
